import { convertToUnit } from '../units_helpers';
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/index';

export type WindSpeedUnit = 'ms' | 'kmh' | 'mph' | 'kt' | 'fts' | 'bft';

const WIND_SPEED_OPTIONS: WindSpeedUnit[] = ['ms', 'kmh', 'mph', 'kt', 'fts', 'bft'];

const MS_TO: Record<Exclude<WindSpeedUnit, 'bft'>, number> = {
	ms: 1,
	kmh: 3.6,
	mph: 2.2369362920544,
	kt: 1.9438444924406,
	fts: 3.2808398950131,
};

/**
 * Converts wind speed from meters per second to a given unit.
 * If the given value is not a finite number, returns NaN.
 * If the unit is 'bft', calls msToBeaufort to convert the value.
 * Otherwise, multiplies the value by the conversion factor stored in MS_TO.
 * @param {number} valueMs - The value to convert in meters per second.
 * @param {WindSpeedUnit} unit - The unit to convert the value to.
 * @returns {number} The converted value.
 */
export const convertWindSpeedFromMs = (valueMs: number, unit: WindSpeedUnit): number => {
	if (!Number.isFinite(valueMs)) return NaN;
	if (unit === 'bft') {
		return msToBeaufort(valueMs);
	}
	return valueMs * MS_TO[unit];
};

const msToBeaufort = (ms: number): number => {
	if (!Number.isFinite(ms) || ms < 0) return NaN;

	// Upper bounds for Beaufort 0..11 in m/s; 12 is >= last bound
	const upperBounds = [0.5, 1.5, 3.3, 5.5, 7.9, 10.7, 13.8, 17.1, 20.7, 24.4, 28.4, 32.6];
	for (let b = 0; b < upperBounds.length; b++) {
		if (ms < upperBounds[b]) return b;
	}
	return 12;
};

export const windSpeedUnitLabel = (unit: WindSpeedUnit): string => {
	switch (unit) {
		case 'ms':
			return 'm/s';
		case 'kmh':
			return 'km/h';
		case 'mph':
			return 'mph';
		case 'kt':
			return 'kt';
		case 'fts':
			return 'ft/s';
		case 'bft':
			return 'Bft';
	}
};

export const getWindUnitOptions = (): SelectOptionPropsType[] => {
	return WIND_SPEED_OPTIONS.map((unit) => ({ title: windSpeedUnitLabel(unit), value: unit }));
};

export const convertWindSpeed = (value: number, unit: WindSpeedUnit): string => {
	if (!Number.isFinite(value)) return '--';
	const windSpeed = convertWindSpeedFromMs(value, unit);
	return convertToUnit(windSpeed, windSpeedUnitLabel(unit));
};
